import type { AllHTMLAttributes, ReactNode } from 'react';

import type { Spacing } from '@shieldpay/theme-provider-ui/theme/types';
import { classFilter } from '@shieldpay/theme-provider-ui/utilities';

import CloseIcon from '../icons/CloseIcon';
import SearchIcon from '../icons/SearchIcon';

import { Box } from '../box/box';
import { Button } from '../button/button';
import { Divider } from '../divider/divider';
import type { FieldContainerBaseProps } from '../field-container/field-container';
import { FieldContainer } from '../field-container/field-container';
import { getInputFieldStyles } from '../input-field/get-input-field-styles';
import { textVariantToStyles } from '../text/text-styles';

export type SearchProps = Omit<FieldContainerBaseProps, 'id'> &
  Partial<
    Pick<
      AllHTMLAttributes<HTMLInputElement>,
      'placeholder' | 'required' | 'autoComplete'
    >
  > & {
    value: string | null;
    onChange: (value: string) => void;
    onClear: () => void;
    clearLabel: ReactNode;
    size?: InputFieldSize;
  };

export type InputFieldSize = keyof typeof sizeConfig;

const sizeConfig = {
  medium: { paddingY: 'baseNeg2' },
  large: { paddingY: 'base' }, // TODO: base should be basePos1, but I don't want to cause regressions on components that use this.
} satisfies {
  [size: string]: {
    paddingY: Spacing;
  };
};

export const Search = ({
  clearLabel,
  label,
  name,
  onChange,
  onClear,
  placeholder,
  value = '',
  size = 'medium',
  screenReaderOnlyLabel = true,
}: SearchProps) => {
  const { inputResetStyles, stateStyles, boxProps } = getInputFieldStyles({
    hasAdjacentSibling: true,
    disabled: false,
    error: false,
  });

  return (
    <FieldContainer
      screenReaderOnlyLabel={screenReaderOnlyLabel}
      name={name}
      htmlFor={name}
      label={label}
    >
      <Box stack="row" className={stateStyles} {...boxProps}>
        <Box<HTMLInputElement>
          Component="input"
          color="currentColor"
          rounded
          expand
          variant="initial"
          className={classFilter([
            inputResetStyles,
            textVariantToStyles('input'),
          ])}
          name={name}
          id={name}
          onChange={({ currentTarget: { value } }) => {
            onChange(value);
          }}
          padding={{
            y: sizeConfig[size].paddingY,
            x: 'base',
          }}
          placeholder={placeholder}
          type="text"
          value={value || ''}
        />

        <Box
          stack="row"
          alignContent={{ x: 'center', y: 'center' }}
          padding={{ x: 'baseNeg3' }}
        >
          {value !== null && value.length > 0 ? (
            <>
              <Button
                onClick={onClear}
                variant="icon"
                Icon={CloseIcon}
                iconColor="neutral500"
              >
                {clearLabel}
              </Button>
              <Box padding={{ y: 'baseNeg2', right: 'baseNeg2' }} expand>
                <Divider type="vertical" color="neutral200" />
              </Box>
            </>
          ) : undefined}

          <SearchIcon color="neutral500" />
        </Box>
      </Box>
    </FieldContainer>
  );
};
